import type { ReactNode } from 'react';

import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

import styles from './Footer.module.css';

export interface FooterLink {
  id: string;
  label: string;
  href: string;
}

export interface FooterProps {
  /** The name of the product, beside the logo. */
  brand: string;
  /** Where the logo leads: the product's front page. Without it the logo is no link. */
  brandHref?: string;
  /** What the logo's link is called for assistive technology. */
  brandLabel?: string;
  /** Pages that are not sections: the front page, the help, the terms. */
  links?: readonly FooterLink[];
  /** Called instead of following a link: for an application that routes itself. */
  onNavigate?: (id: string) => void;
  /** Name of the links' navigation landmark. */
  navLabel?: string;
  /** Controls of the application's own, such as a way to report a problem. */
  actions?: ReactNode;
  /** The small print under the rest: the year and whose the product is. */
  note?: ReactNode;
  className?: string;
}

/**
 * The foot of every page of an application: whose it is, the pages that are not
 * sections, and a place for a control or two. It wraps on a narrow screen instead of
 * running off it.
 */
export function Footer({
  brand,
  brandHref,
  brandLabel,
  links = [],
  onNavigate,
  navLabel = 'Footer',
  actions,
  note,
  className,
}: FooterProps) {
  const Brand = brandHref ? 'a' : 'span';
  return (
    <footer className={cx(styles.footer, className)}>
      <div className={styles.row}>
        <Brand className={styles.brand} {...(brandHref ? { href: brandHref, 'aria-label': brandLabel, title: brandLabel } : {})}>
          <Icon name="logo" size={20} className={styles.logo} />
          <span>{brand}</span>
        </Brand>
        {links.length > 0 ? (
          <nav aria-label={navLabel}>
            <ul className={styles.links}>
              {links.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    className={styles.link}
                    onClick={(event) => {
                      if (!onNavigate) return;
                      event.preventDefault();
                      onNavigate(link.id);
                    }}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      {note ? <p className={styles.note}>{note}</p> : null}
    </footer>
  );
}
